import React from 'react';
import { DateRange } from 'plume-ui/dist/components/InputField/DateRangeInputField';
import FormattedMessage from '../../../../../../utils/components/FormattedMessage';
import {
  OutreachTimeFormFields,
  TimeDropdownProperties,
} from '../../../../types';
import { DateRangeSelect } from 'plume-ui';
import OutreachConfigFieldMessages from '../../../../../Outreach/components/OutreachConfigFieldMessages';
import useFormControls from '../../hooks/useFormControls';
import { FormikOutreachSupportTimeValue } from './support/FormikOutreachSupportTimeField';
import { useTranslation } from 'react-i18next';
import { FieldInputProps, FormikProps } from 'formik';
import { Maybe } from '../../../../../../types';
import dayjs from 'dayjs';
import { STANDARD_DATE_TIME_FORMAT } from '../../../../../../config';

type AvailableValuesDefinitions = FormikOutreachSupportTimeValue;

export type DateTimeFieldProps<T extends AvailableValuesDefinitions> = {
  field: FieldInputProps<T>;
  form: FormikProps<T>;
  property: TimeDropdownProperties;
  handleDropdownSelection: (
    property: TimeDropdownProperties,
    value: Maybe<string>,
  ) => void;
  value?: DateRange | Date | null;
};

const mapPropertyToFallbackLabel: Record<string, string> = {
  [OutreachTimeFormFields.From]:
    'outreach.form.supportOutreachTimeSection.selectDate',
  [OutreachTimeFormFields.To]:
    'outreach.form.supportOutreachTimeSection.neverEnds',
};

const NEVER_ENDS = 'never-ends';

function DateTimeFieldRenderer<T extends AvailableValuesDefinitions>({
  property,
  field,
  form,
  handleDropdownSelection,
}: DateTimeFieldProps<T>) {
  const { t } = useTranslation();
  const { getPropertyValue } = useFormControls<T>(field, form);
  const value = (getPropertyValue(property) as unknown) as
    | Date
    | DateRange
    | null;
  const label = value
    ? //@ts-expect-error
      `${dayjs(value).local().format(STANDARD_DATE_TIME_FORMAT)}`
    : t(mapPropertyToFallbackLabel[property]);
  const extraOptions =
    property === 'from'
      ? []
      : [
          {
            id: NEVER_ENDS,
            label: t('outreach.form.supportOutreachTimeSection.neverEnds'),
            isSelected: (range: DateRange) => range == null,
          },
        ];

  const onSelect = (range: DateRange | Date | null) => {
    handleDropdownSelection(property, range ? range.toString() : undefined);
  };

  return (
    <div className="OutreachContainer__subscriber-outreach-time-field">
      <span>
        {property === OutreachTimeFormFields.From && (
          <div>
            <span className="color-required">* </span>
            <FormattedMessage id="outreach.form.supportOutreachTimeSection.validFrom" />
          </div>
        )}
        {property === OutreachTimeFormFields.To && (
          <FormattedMessage id="outreach.form.supportOutreachTimeSection.to" />
        )}
      </span>
      <DateRangeSelect
        position="right"
        show={false}
        openInPortal
        onSelect={onSelect}
        singleDate
        showTimeSelect
        extraOptions={extraOptions}
        label={label}
        value={value}
      />
      <OutreachConfigFieldMessages
        propertyName={property}
        field={field}
        form={form}
      />
    </div>
  );
}

export default DateTimeFieldRenderer;
